import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { db } from "./firebase";
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  increment, 
  deleteDoc,
  query,
  orderBy
} from "firebase/firestore";

// ─── Initial Data ──────────────────────────────────────────────────────────
const INITIAL_ARTICLES = [
  {
    id: "bc-trans-1",
    title: "Why a person should shift on blockchain transaction mode?",
    subtitle: "Speed, Security, and the Future of Finance",
    contentHtml: `<p>For a regular person, the “why” usually boils down to three technical advantages that traditional banks (centralized systems) struggle to match:</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/1024/0*eCCFHaYEPjgC-Hsh" /></figure>
<ul>
  <li><strong>Settlement Speed (T+0):</strong> In traditional banking, a cross-border transfer can take 3–5 days to “settle” because multiple banks have to talk to each other. On a blockchain, the transaction and the settlement happen <strong>simultaneously</strong>. Once the block is mined, the money is there.</li>
  <li><strong>Programmability (Smart Contracts):</strong> You can’t tell a 10-dollar bill to “only spend yourself if the weather is 30°C.” You <em>can</em> do that with blockchain. This allows for automated escrow, streaming salaries (getting paid by the second), and automated refunds.</li>
  <li><strong>24/7/365 Availability:</strong> Blockchains don’t have “bank holidays” or “working hours.” The network is global and always on.</li>
  <li><strong>Self-Sovereignty:</strong> You are your own bank. No central authority can “freeze” your account because of a technical glitch or a policy change, provided you manage your private keys securely.</li>
</ul>
<h3>Live Projects Used by the Public (2026)</h3>
<p>Blockchain has moved past the “experimental” phase. Here are verified applications being used by the public and major institutions today.</p>
<h3>Institutional Finance & Investment</h3>
<ul>
  <li><strong>JPM Coin (JPMorgan):</strong> Enables large corporations to move billions of dollars across international accounts instantly, 24/7, bypassing traditional banking delays.</li>
  <li><strong>BlackRock BUIDL:</strong> A tokenized fund on the Ethereum blockchain that provides institutional investors with instant liquidity and yield on US Treasuries.</li>
</ul>
<h3>Supply Chain & Global Trade</h3>
<ul>
  <li><strong>Tracr (De Beers):</strong> Uses blockchain to track the provenance of diamonds, ensuring they are conflict-free and authentic from mine to finger.</li>
  <li><strong>IBM Food Trust:</strong> Major retailers like Walmart use this to track food items. In case of an outbreak, they can trace a head of lettuce back to its specific farm in seconds instead of weeks.</li>
</ul>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">🚀 Roadmap: Shifting to Blockchain</h3>
  <ul>
    <li>[ ] Understand Wallets & Private Keys (Self-Custody)</li>
    <li>[ ] Explore Layer 2 Solutions (L2s) for low fees</li>
    <li>[ ] Transition liquid assets to regulated stablecoins (USDC/PYUSD)</li>
    <li>[ ] Implement smart contract automation for monthly bills</li>
  </ul>
</div>`,
    content: `For a regular person, the “why” usually boils down to three technical advantages that traditional banks (centralized systems) struggle to match:

![Blockchain Advantages](https://cdn-images-1.medium.com/max/1024/0*eCCFHaYEPjgC-Hsh)

- **Settlement Speed (T+0):** In traditional banking, a cross-border transfer can take 3–5 days to “settle”. On a blockchain, the transaction and the settlement happen **simultaneously**.
- **Programmability (Smart Contracts):** You can program money to "only spend if the weather is 30°C."
- **24/7/365 Availability:** No “bank holidays” or “working hours.”
- **Self-Sovereignty:** You are your own bank.

### Live Projects Used by the Public (2026)
Blockchain has moved past the “experimental” phase...

#### Institutional Finance & Investment
- **JPM Coin (JPMorgan):** Move billions instantly.
- **BlackRock BUIDL:** Tokenized funds with instant liquidity.

#### 🚀 Roadmap: Shifting to Blockchain
- [ ] Understand Wallets & Private Keys
- [ ] Explore Layer 2 Solutions
- [ ] Transition liquid assets to stablecoins`,
    excerpt: "The 'why' of blockchain boils down to technical advantages that traditional banks struggle to match: Speed, Programmability, and Sovereignty.",
    tags: ["blockchain", "finance", "tech"],
    slug: "blockchain-transaction-mode",
    publishedAt: "2026-04-26T07:43:50Z",
    issueNumber: 10
  },
  {
    id: "bc-shift-2",
    title: "Why Shift to Blockchain?",
    subtitle: "Practical Directions for dApps",
    contentHtml: `<p>Building an end-to-end blockchain application (dApp) is a fantastic way to master the integration of smart contracts with modern web frameworks.</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/900/1*GugYmacC0Pr8I8a3fKBefA.png" /></figure>
<p>Here are four <strong>distinct directions</strong> ranging from supply chain to finance, each designed to showcase a full-stack technical grasp.</p>
<h3>1. Decentralized Milestone-Based Freelance Marketplace</h3>
<p>Traditional freelance platforms often have high fees and dispute issues. A blockchain version uses an <strong>Escrow Smart Contract</strong> to hold funds and release them only when specific milestones are met.</p>
<ul>
  <li><strong>The Tech Stack:</strong> Solidity (Smart Contracts), Hardhat (Development Environment), React/Next.js (Frontend), and Ethers.js (Blockchain interaction).</li>
  <li><strong>Key Feature:</strong> A “Dispute Resolution” mechanism where a DAO can vote on project completion.</li>
</ul>
<h3>2. Transparent Supply Chain for Luxury Goods</h3>
<p>Counterfeiting is a trillion-dollar problem. This app tracks a product’s journey from the manufacturer to the end consumer using <strong>NFTs as “Digital Twins.”</strong></p>
<ul>
  <li><strong>The Tech Stack:</strong> Polygon (for low gas fees), IPFS/Filecoin (to store product images/metadata), and a Mobile App (React Native) for scanning.</li>
  <li><strong>Key Feature:</strong> QR code integration for ownership history verification.</li>
</ul>
<h3>3. Parametric Insurance Protocol</h3>
<p>Automate insurance payouts using **Oracles** (like Chainlink) to trigger payments based on real-world data (e.g., flight delays or weather events).</p>
<h3>4. P2P Energy Trading Grid</h3>
<p>Allow neighbors to trade solar energy directly using smart contracts for automated billing and grid management.</p>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">🛠️ Developer Cheatsheet</h3>
  <table style="width:100%; font-size:0.9rem;">
    <tr><td style="font-weight:bold">Frontend</td><td>React + Ethers.js</td></tr>
    <tr><td style="font-weight:bold">Backend</td><td>Solidity + Hardhat</td></tr>
    <tr><td style="font-weight:bold">Storage</td><td>IPFS (Decentralized)</td></tr>
    <tr><td style="font-weight:bold">Network</td><td>Polygon / Arbitrum (L2)</td></tr>
  </table>
</div>`,
    content: `Building an end-to-end blockchain application (dApp) is a fantastic way to master the integration of smart contracts with modern web frameworks.

![dApp Directions](https://cdn-images-1.medium.com/max/900/1*GugYmacC0Pr8I8a3fKBefA.png)

### 1. Decentralized Milestone-Based Freelance Marketplace
### 2. Transparent Supply Chain for Luxury Goods
### 3. Parametric Insurance Protocol
### 4. P2P Energy Trading Grid`,
    excerpt: "Building a dApp is the best way to master Web3. Explore four directions: Freelance Marketplaces, Supply Chain NFTs, Parametric Insurance, and P2P Energy.",
    tags: ["blockchain", "dapps", "web3"],
    slug: "why-shift-to-blockchain",
    publishedAt: "2026-04-26T07:33:07Z",
    issueNumber: 9
  },
  {
    id: "ai-agents-3",
    title: "AI Agents Vs Agentic AI",
    subtitle: "From Workers to Orchestrators",
    contentHtml: `<figure><img alt="" src="https://cdn-images-1.medium.com/max/878/1*TszNiJL72vK23OW8-ieevA.png" /></figure>
<p>While the terms are often used interchangeably, in the landscape of 2026, the industry has drawn a clear distinction between them. Think of <strong>AI Agents</strong> as the specialized workers and <strong>Agentic AI</strong> as the intelligent management system that empowers them.</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/275/0*TE9QeNxY5mB4slge" /><figcaption>AI Agents Vs Agentic AI</figcaption></figure>
<h3>1. AI Agents: The “Worker Bees”</h3>
<p>An AI Agent is a specific software entity designed to perform tasks. It has access to “tools” (like an API, a database, or a web browser) and uses an LLM to decide which tool to use to fulfill a request.</p>
<ul>
  <li><strong>Characteristics:</strong> Narrow scope, follows a specific “persona,” and is usually triggered by a human.</li>
  <li><strong>Example:</strong> A <strong>Support Agent</strong> that can look up a customer’s order and process a refund when a user asks for it.</li>
</ul>
<h3>2. Agentic AI: The “Orchestrator”</h3>
<p>Agentic AI describes an entire system (or an AI behavior) that demonstrates true <strong>agency</strong>. It doesn’t just follow instructions; it creates its own sub-tasks, plans for long-term goals, and monitors its own progress.</p>
<ul>
  <li><strong>Characteristics:</strong> High autonomy, situational awareness, and the ability to coordinate multiple “AI Agents.”</li>
  <li><strong>Example:</strong> An <strong>Agentic Supply Chain System</strong>. It predict a logistics bottleneck and independently directs various <em>Logistics Agents</em> to reroute shipments.</li>
</ul>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">💡 2026 Shift: From Tools to Ecosystems</h3>
  <p>We are seeing a move away from standalone agents toward <strong>Agentic Orchestration</strong>. Standardized protocols like **MCP (Model Context Protocol)** are becoming the backbone of these autonomous systems.</p>
</div>`,
    content: `Think of AI Agents as the specialized workers and Agentic AI as the intelligent management system that empowers them.

![Agentic AI](https://cdn-images-1.medium.com/max/878/1*TszNiJL72vK23OW8-ieevA.png)

### 1. AI Agents: The “Worker Bees”
### 2. Agentic AI: The “Orchestrator”`,
    excerpt: "Distinguish between specialized 'Worker Bee' AI Agents and the autonomous 'Orchestrator' of Agentic AI. 2026 is the year of agency.",
    tags: ["ai", "agents", "future"],
    slug: "ai-agents-vs-agentic-ai",
    publishedAt: "2026-01-09T16:27:48Z",
    issueNumber: 8
  },
  {
    id: "prompt-eng-4",
    title: "Prompt Engineering",
    subtitle: "Optimizing the LLM Interface",
    contentHtml: `<p>It is essentially the “API documentation” for talking to AI models.</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/1024/0*Jw8uaSN6IzJT5NWN" /></figure>
<h3>What is Prompt Engineering?</h3>
<p><strong>Prompt Engineering</strong> is the process of designing, refining, and optimizing inputs (prompts) to guide Large Language Models (LLMs) like Gemini, GPT-4, or Claude to produce the most accurate, high-quality, and useful results possible.</p>
<h3>1. The Core Techniques</h3>
<p>As a developer, you’ll likely use these specific patterns when building your Micro-SaaS or automating tasks:</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/873/1*1DlqWI3q4LO-YekJ_C1JuA.png" /></figure>
<p>Understanding <strong>Prompt Engineering</strong> is actually one of the most valuable “meta-skills” you can have in 2026. It is essentially the “API documentation” for talking to AI models.</p>
<h3>2. Anatomy of a “Pro” Prompt</h3>
<p>A high-level prompt is rarely just a sentence. It usually follows a structured framework.</p>
<blockquote style="border-left:4px solid var(--rust); padding-left:16px; margin:20px 0">
  <strong>The Framework:</strong>
  <ul>
    <li><strong>Role:</strong> “You are an expert React developer...”</li>
    <li><strong>Context:</strong> “I have a legacy dashboard...”</li>
    <li><strong>Task:</strong> “Analyze the provided code...”</li>
    <li><strong>Constraints:</strong> “Do not use external libraries...”</li>
    <li><strong>Output Format:</strong> “Return valid JSON...”</li>
  </ul>
</blockquote>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">🧠 Prompting Cheatsheet</h3>
  <ul>
    <li><strong>Zero-Shot:</strong> Direct instruction without examples.</li>
    <li><strong>Few-Shot:</strong> Providing 2-3 examples of input/output.</li>
    <li><strong>Chain of Thought:</strong> "Think step by step."</li>
    <li><strong>DSPy:</strong> Programmatic prompt optimization.</li>
  </ul>
</div>`,
    content: `Prompt Engineering is the API documentation for AI.

![Prompt Engineering](https://cdn-images-1.medium.com/max/1024/0*Jw8uaSN6IzJT5NWN)

### What is Prompt Engineering?
### 1. The Core Techniques
### 2. Anatomy of a “Pro” Prompt`,
    excerpt: "Prompt engineering is the meta-skill of 2026. Learn the anatomy of a pro prompt and core optimization techniques.",
    tags: ["ai", "productivity", "tech"],
    slug: "prompt-engineering",
    publishedAt: "2026-01-03T07:37:06Z",
    issueNumber: 7
  },
  {
    id: "smile-csr-5",
    title: "Smile Foundation CSR",
    subtitle: "Maternal Health Initiative in Maharashtra",
    contentHtml: `<p>Visited a place in Maharashtra to promote Breast-Feeding awareness!</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/170/1*CUB3cDzvzjVvllzWzgB6Eg.jpeg" /></figure>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/1024/1*JTnDZkTJ3JANd80kFuVDJQ.jpeg" /></figure>
<p>As long as health goes, most vulnerable population segments in rural India are women & senior citizens. Poor health of a woman has a direct impact on the future society.</p>
<p>Recognising this, we focus on improving the health of mothers and mothers-to-be. We ensure that their health parameters remain as healthy as possible in the eco-system in which they live.</p>
<p>Over the years, the results we have achieved are:</p>
<ul>
  <li>72% of adolescent girls have higher Hb</li>
  <li>100% Antenatal care ensuring proper growth of the baby</li>
  <li>100% Institutional & safe deliveries</li>
  <li>Average weight at birth increased from 2 kg to 2.6 kg</li>
</ul>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">📊 Project Highlights</h3>
  <ul>
    <li>Network of 48 Community Health Workers.</li>
    <li>Kitchen gardens for nutritional support.</li>
    <li>Reduced malnutrition from 42% to 35%.</li>
  </ul>
</div>`,
    content: `Visited Maharashtra to promote Breast-Feeding awareness!

![Impact](https://cdn-images-1.medium.com/max/1024/1*JTnDZkTJ3JANd80kFuVDJQ.jpeg)`,
    excerpt: "Improving the health of mothers and children in rural Maharashtra through 100% safe deliveries and health worker networks.",
    tags: ["csr", "impact", "maharashtra"],
    slug: "smile-foundation-csr",
    publishedAt: "2024-09-07T09:53:38Z",
    issueNumber: 6
  },
  {
    id: "shingawadi-6",
    title: "Shingawadi, Maharashtra",
    subtitle: "Resilience in Tribal Communities",
    contentHtml: `<h3>Shingawadi, Maharashtra</h3><p>Beautiful Visit in nature …</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/259/1*OOF1iQQQceDgOTJ6LM6QUg.jpeg" /></figure>
<p>Farmers are severely affected due to a lack of rainfall. 60% of the farmers are dependent on money lenders. In response to the influx of rural migrants during the pandemic, ACF initiated Terrace Farming in the hilly area of Jiwati.</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/448/1*VI_LHOXe5F7PLqyyLpvZIA.jpeg" /></figure>
<p>I remember visiting an old age home, spending one full day with these people... one lady asked with a shivering voice: "When will you come back?"</p>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">🌱 Terrace Farming Impact</h3>
  <p>Increased net income by Rs. 20,000 to Rs. 25,000 for local farmers while preventing soil erosion in hilly terrains.</p>
</div>`,
    content: `Beautiful visit in nature. Responding to rainfall challenges with Terrace Farming.`,
    excerpt: "A visit to Shingawadi revealed the power of Terrace Farming and the deep emotional connections formed during CSR activities.",
    tags: ["travel", "social-good", "maharashtra"],
    slug: "shingawadi-maharashtra",
    publishedAt: "2024-09-07T09:48:06Z",
    issueNumber: 5
  },
  {
    id: "csr-concepts-7",
    title: "Corporate Social Responsibility",
    subtitle: "A day given back to society",
    contentHtml: `<p>A day given back to society ….</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/1024/1*TpojLG2HZEkvmnn59CkMqQ.jpeg" /><figcaption>Shree Mesai Devi Temple, Kanhur village, Maharashtra</figcaption></figure>
<p>Our team visited Kanhur Mesai Village to contribute in farming and healthy Kitchen gardens. We saw custard apple & onion seeds there, praised them, and celebrated their achievements.</p>
<h3>What Is CSR?</h3>
<p>Corporate social responsibility (CSR) is a <strong>self-regulating business model</strong> that helps a company be socially accountable to itself, its stakeholders, and the public.</p>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">⭐ Key Takeaways</h3>
  <ul>
    <li>CSR helps improve society and promote a positive brand image.</li>
    <li>Categories: Environmental, Ethical, Philanthropic, and Financial.</li>
  </ul>
</div>`,
    content: `Giving back to society at Kanhur Mesai Village. What is CSR?`,
    excerpt: "CSR is more than a buzzword; it's a self-regulating model that enhances society and the environment through accountability.",
    tags: ["business", "csr", "sustainability"],
    slug: "corporate-social-responsibility",
    publishedAt: "2024-09-07T09:33:41Z",
    issueNumber: 4
  },
  {
    id: "bit-git-8",
    title: "Bitbucket vs GitHub",
    subtitle: "Choosing the Right Repository",
    contentHtml: `<p>Choosing a repository hosting service is a big decision. It affects your productivity and how you host your code.</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/1012/0*oU7To-ep1ImdveGf.png" /></figure>
<p>GitHub is a powerful open source platform for personal or small team projects. Bitbucket is a cost-effective solution for enterprises looking for safe hosting for private proprietary code.</p>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">⚖️ Quick Comparison</h3>
  <table style="width:100%">
    <tr><td>GitHub</td><td>Best for Open Source & Ecosystem</td></tr>
    <tr><td>Bitbucket</td><td>Best for Enterprise & Jira Sync</td></tr>
  </table>
</div>`,
    content: `GitHub vs Bitbucket. Open source vs Private Enterprise.`,
    excerpt: "Which code repository is the best choice? Compare VCS support, collaborator access, and integrations.",
    tags: ["devops", "git", "tools"],
    slug: "bitbucket-vs-github",
    publishedAt: "2024-05-01T20:04:41Z",
    issueNumber: 3
  },
  {
    id: "liq-graph-9",
    title: "Liquibase & GraphQL",
    subtitle: "Modern Data Management",
    contentHtml: `<p>The Liquibase API consists of pluggable interfaces and driving logic. It helps us create changelog.xml files to apply SQL queries on each environment.</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/273/0*qDNttHo1hZ4xLUk8" /></figure>
<h3>What is a GraphQL API?</h3>
<p>A GraphQL API transforms how clients access data. Unlike REST, GraphQL allows clients to specify the required data and obtain it in a structured format.</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/1024/0*jeGAI8LM27nILJub.png" /></figure>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">🏗️ Implementation</h3>
  <p>Start with a <strong>GraphQL Schema</strong>. It describes data types and relationships using GSL.</p>
</div>`,
    content: `Liquibase for database versioning and GraphQL for flexible API queries.`,
    excerpt: "Combine systematic database migrations with flexible data retrieval to build modern, scalable APIs.",
    tags: ["database", "api", "graphql"],
    slug: "liquibase-graphql",
    publishedAt: "2024-04-28T03:45:03Z",
    issueNumber: 2
  },
  {
    id: "london-10",
    title: "London Tour: blog 1",
    subtitle: "Panoramic views from the London Eye",
    contentHtml: `<p>I wanted to share my experience when I travelled to London -</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/1024/1*JikhWRkUJGkFGJbHGQHo8A.jpeg" /><figcaption>A Place to visit</figcaption></figure>
<p>London is packed with museums and architecture. Why not start with the London Eye for panoramic views? It stands at 135 meters high, the world’s largest cantilevered observation wheel.</p>
<figure><img alt="" src="https://cdn-images-1.medium.com/max/1024/1*OF07rVY1Uy7dWH4NoxIFTg.jpeg" /></figure>
<div style="background:var(--cream); padding:24px; border-radius:8px; margin-top:40px; border:1px solid var(--border);">
  <h3 style="margin-top:0">🎡 London Eye Insights</h3>
  <ul>
    <li>Launched in 2000.</li>
    <li>Over 85 awards for tourism and engineering.</li>
    <li>Panoramic views up to 25 miles in every direction.</li>
  </ul>
</div>`,
    content: `Sharing my experience traveling to London. Exploring the London Eye.`,
    excerpt: "Kickstart your London adventure with breathtaking views from the world's largest cantilevered observation wheel.",
    tags: ["travel", "london", "engineering"],
    slug: "london-tour-blog-1",
    publishedAt: "2024-01-06T15:49:57Z",
    issueNumber: 1
  }
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const ADMIN_PASSWORD = "adosi12";

const slugify = (t) =>
  t
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const readTime = (text) =>
  Math.max(1, Math.ceil(text.split(/\s+/).length / 200));

// ─── Firebase Storage Helpers ───────────────────────────────────────────────
async function fetchArticles() {
  const q = query(collection(db, "articles"), orderBy("issueNumber", "desc"));
  const snapshot = await getDocs(q);
  const fetched = snapshot.docs.map(doc => doc.data());
  return fetched.length > 0 ? fetched : INITIAL_ARTICLES;
}

async function fetchSiteSettings() {
  const docRef = doc(db, "settings", "site");
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : {
    title: "The Daily Dispatch",
    tagline: "Thoughts, ideas & observations — delivered fresh.",
    author: "Aditi Dosi",
  };
}

async function saveArticleToCloud(article) {
  await setDoc(doc(db, "articles", article.id), article);
}

async function deleteArticleFromCloud(id) {
  await deleteDoc(doc(db, "articles", id));
}

async function saveSettingsToCloud(settings) {
  await setDoc(doc(db, "settings", "site"), settings);
}

async function incrementViewCount(slug) {
  const docRef = doc(db, "views", slug);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    await setDoc(docRef, { count: 1 });
  } else {
    await updateDoc(docRef, { count: increment(1) });
  }
}

async function fetchAllViews() {
  const snapshot = await getDocs(collection(db, "views"));
  const views = {};
  snapshot.forEach(doc => {
    views[doc.id] = doc.data().count;
  });
  return views;
}

// ─── Markdown to HTML ─────────────────────────────────────────────────────────
function mdToHtml(text) {
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^> (.+)$/gm, "<blockquote>$1</blockquote>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank">$1</a>')
    .replace(/^- (.+)$/gm, "<li>$1</li>");

  html = html.replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`);

  const lines = html.split("\n");
  const result = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) { result.push(""); continue; }
    if (/^<(h[23]|blockquote|ul|li)/.test(trimmed)) { result.push(trimmed); continue; }
    result.push(`<p>${trimmed}</p>`);
  }
  return result.join("\n");
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PostNumber({ n }) {
  return (
    <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.72rem", color: "var(--muted)", letterSpacing: "0.05em" }}>
      № {String(n).padStart(3, "0")}
    </span>
  );
}

function Header({ settings, isAdmin, onAdmin }) {
  return (
    <header style={{ borderBottom: "1px solid var(--border)", background: "var(--paper)", position: "sticky", top: 0, zIndex: 50 }}>
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
        <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontSize: "1.35rem", letterSpacing: "-0.01em", lineHeight: 1 }}>
            {settings.title}
          </div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.62rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginTop: 2 }}>
            Personal Blog
          </div>
        </Link>
        <nav style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link to="/archive" style={{ textDecoration: "none", color: "var(--muted)", fontSize: "0.85rem", fontWeight: 500 }}>Archive</Link>
          {isAdmin ? (
            <>
              <Link to="/analytics" className="btn btn-ghost" style={{ fontSize: "0.8rem", textDecoration: "none" }}>📊 Analytics</Link>
              <Link to="/write" className="btn btn-primary" style={{ fontSize: "0.8rem", textDecoration: "none" }}>✏️ Write</Link>
              <Link to="/settings" className="btn btn-ghost" style={{ fontSize: "0.8rem", textDecoration: "none" }}>⚙️</Link>
            </>
          ) : (
            <button className="btn btn-ghost" style={{ fontSize: "0.8rem" }} onClick={onAdmin}>Admin</button>
          )}
        </nav>
      </div>
    </header>
  );
}

function ArticleCard({ article, index, views, isAdmin, onEdit, onDelete }) {
  const isFeature = index === 0;
  return (
    <article
      className="fade-in"
      style={{ animationDelay: `${index * 80}ms`, borderBottom: "1px solid var(--border)", padding: isFeature ? "40px 0 32px" : "28px 0" }}
    >
      {isFeature && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--rust)", fontWeight: 500 }}>
            ★ Latest Post
          </div>
          <div className="divider" style={{ flex: 1 }} />
        </div>
      )}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24 }}>
        <div style={{ flex: 1 }}>
          <Link to={`/article/${article.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              <PostNumber n={article.issueNumber} />
              {article.tags?.map((t) => <span key={t} className="tag">{t}</span>)}
              <span style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{formatDate(article.publishedAt)}</span>
            </div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontWeight: isFeature ? 900 : 700, fontSize: isFeature ? "2rem" : "1.35rem", lineHeight: 1.25, marginBottom: 10 }}>
              {article.title}
            </h2>
            {article.subtitle && (
              <p style={{ fontStyle: "italic", color: "var(--muted)", marginBottom: 12, fontSize: "1.05rem" }}>{article.subtitle}</p>
            )}
            <p style={{ color: "#4a453e", lineHeight: 1.7, fontSize: "0.95rem" }}>{article.excerpt}</p>
            <div style={{ marginTop: 14, display: "flex", gap: 16 }}>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.72rem", color: "var(--muted)" }}>{readTime(article.content)} min read</span>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.72rem", color: "var(--muted)" }}>{views} views</span>
            </div>
          </Link>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
            <Link to={`/write?editId=${article.id}`} className="btn btn-ghost" style={{ fontSize: "0.75rem", padding: "5px 12px", textDecoration: "none" }}>Edit</Link>
            <button className="btn btn-danger" style={{ fontSize: "0.75rem", padding: "5px 12px" }} onClick={() => onDelete(article.id)}>Delete</button>
          </div>
        )}
      </div>
    </article>
  );
}

function HomeView({ articles, settings, isAdmin, viewCounts, onDelete }) {
  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "0 24px" }}>
      <Helmet>
        <title>{settings.title}</title>
        <meta name="description" content={settings.tagline} />
      </Helmet>
      <section className="fade-in" style={{ padding: "60px 0 40px", borderBottom: "2px solid var(--ink)" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
          <div>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontSize: "clamp(2.5rem,6vw,4.5rem)", lineHeight: 1, letterSpacing: "-0.02em", marginBottom: 16 }}>
              {settings.title}
            </h1>
            <p style={{ fontSize: "1.1rem", color: "var(--muted)", maxWidth: 500, lineHeight: 1.6 }}>{settings.tagline}</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.72rem", color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              By {settings.author}
            </div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.72rem", color: "var(--muted)", marginTop: 4 }}>
              {articles.length} post{articles.length !== 1 ? "s" : ""} published
            </div>
          </div>
        </div>
      </section>

      <section>
        {articles.length === 0 ? (
          <div style={{ padding: "80px 0", textAlign: "center", color: "var(--muted)" }}>
            <div style={{ fontSize: "3rem", marginBottom: 16 }}>✍️</div>
            <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.5rem" }}>No articles yet.</p>
            {isAdmin && <p style={{ marginTop: 8, fontSize: "0.9rem" }}>Click "Write" to publish your first post.</p>}
          </div>
        ) : (
          articles.map((a, i) => (
            <ArticleCard
              key={a.id}
              article={a}
              index={i}
              views={viewCounts[a.slug] || 0}
              isAdmin={isAdmin}
              onDelete={onDelete}
            />
          ))
        )}
      </section>

      <footer style={{ padding: "40px 0", textAlign: "center", color: "var(--muted)", fontSize: "0.82rem", borderTop: "1px solid var(--border)", marginTop: 20 }}>
        <span style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic" }}>{settings.title}</span> · Made with intention
      </footer>
    </div>
  );
}

function ArticleView({ articles, settings, onRecordView }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const article = articles.find(a => a.slug === slug);

  useEffect(() => { 
    if (article) {
      onRecordView(article.slug);
      window.scrollTo(0, 0);
    }
  }, [article?.slug]);

  if (!article) return <div style={{ padding: 80, textAlign: "center" }}>Article not found. <button onClick={() => navigate('/')}>Go Home</button></div>;

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.subtitle,
        url: url,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "0 24px" }}>
      <Helmet>
        <title>{article.title} | {settings.title}</title>
        <meta name="description" content={article.excerpt} />
      </Helmet>
      <div style={{ padding: "32px 0 0" }}>
        <button className="btn btn-ghost" style={{ fontSize: "0.8rem", marginBottom: 32 }} onClick={() => navigate('/')}>
          ← Back to all articles
        </button>
      </div>
      <article className="fade-in" style={{ maxWidth: 700 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          <PostNumber n={article.issueNumber} />
          {article.tags?.map((t) => <span key={t} className="tag">{t}</span>)}
        </div>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontSize: "clamp(2rem,5vw,3.2rem)", lineHeight: 1.15, letterSpacing: "-0.02em", marginBottom: 12 }}>
          {article.title}
        </h1>
        {article.subtitle && (
          <p style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", fontSize: "1.25rem", color: "var(--muted)", marginBottom: 20 }}>
            {article.subtitle}
          </p>
        )}
        <div style={{ display: "flex", gap: 20, alignItems: "center", padding: "16px 0", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", marginBottom: 40, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.78rem", color: "var(--muted)" }}>{settings.author}</span>
          <span style={{ color: "var(--border)" }}>·</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.78rem", color: "var(--muted)" }}>{formatDate(article.publishedAt)}</span>
          <span style={{ color: "var(--border)" }}>·</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.78rem", color: "var(--muted)" }}>{readTime(article.content)} min read</span>
          <span style={{ color: "var(--border)" }}>·</span>
          <button 
            onClick={handleShare}
            style={{ 
              background: "none", 
              border: "none", 
              fontFamily: "'DM Mono',monospace", 
              fontSize: "0.78rem", 
              color: "var(--rust)", 
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: 0
            }}
          >
            {copied ? "✓ Copied!" : "🔗 Share"}
          </button>
        </div>
        <div className="prose" dangerouslySetInnerHTML={{ __html: article.contentHtml }} />
        <div style={{ marginTop: 48, padding: "24px 0", borderTop: "1px solid var(--border)" }}>
          <p style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", color: "var(--muted)" }}>— {settings.author}</p>
        </div>
      </article>
      <footer style={{ padding: "40px 0", color: "var(--muted)", fontSize: "0.82rem", borderTop: "1px solid var(--border)", marginTop: 20, textAlign: "center" }}>
        <span style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic" }}>{settings.title}</span> ·{" "}
        <Link to="/" style={{ color: "var(--rust)", textDecoration: "underline" }}>
          Read more articles
        </Link>
      </footer>
    </div>
  );
}

function ArchiveView({ articles, settings }) {
  const grouped = articles.reduce((acc, a) => {
    const year = new Date(a.publishedAt).getFullYear();
    if (!acc[year]) acc[year] = [];
    acc[year].push(a);
    return acc;
  }, {});

  const years = Object.keys(grouped).sort((a, b) => b - a);

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "0 24px" }}>
      <Helmet>
        <title>Archive | {settings.title}</title>
        <meta name="description" content={`Explore all published posts of ${settings.title}.`} />
      </Helmet>
      <div style={{ padding: "60px 0 40px", borderBottom: "2px solid var(--ink)" }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontSize: "clamp(2.5rem,6vw,4rem)", lineHeight: 1, letterSpacing: "-0.02em", marginBottom: 16 }}>
          Archive
        </h1>
        <p style={{ fontSize: "1.1rem", color: "var(--muted)", maxWidth: 500, lineHeight: 1.6 }}>A complete collection of all shared thoughts and ideas.</p>
      </div>

      <div style={{ padding: "40px 0" }}>
        {years.length === 0 ? (
          <p style={{ textAlign: "center", color: "var(--muted)", padding: "40px 0" }}>No articles in the archive yet.</p>
        ) : (
          years.map(year => (
            <div key={year} style={{ marginBottom: 48 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "2rem", borderBottom: "1px solid var(--border)", paddingBottom: 12, marginBottom: 24 }}>{year}</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {grouped[year].map(a => (
                  <Link key={a.id} to={`/article/${a.slug}`} style={{ textDecoration: "none", color: "inherit", display: "flex", alignItems: "baseline", gap: 16 }}>
                    <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.8rem", color: "var(--muted)", width: 100, flexShrink: 0 }}>
                      {new Date(a.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span style={{ fontSize: "1.15rem", fontWeight: 500, borderBottom: "1px solid transparent", transition: "border-color 0.2s" }} onMouseOver={(e) => e.target.style.borderColor = "var(--ink)"} onMouseOut={(e) => e.target.style.borderColor = "transparent"}>
                      {a.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <footer style={{ padding: "40px 0", textAlign: "center", color: "var(--muted)", fontSize: "0.82rem", borderTop: "1px solid var(--border)", marginTop: 20 }}>
        <span style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic" }}>{settings.title}</span> · Archive
      </footer>
    </div>
  );
}

function MediaUploader({ onInsert }) {
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [showExternal, setShowExternal] = useState(false);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (selected.size > 500 * 1024) {
      setError("File too large (>500KB). Please use an external link.");
      setShowExternal(true);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      const isPdf = selected.type === "application/pdf";
      const snippet = isPdf 
        ? `[View PDF](${base64})` 
        : `![${selected.name}](${base64})`;
      onInsert(snippet);
      setFile(null);
      setError("");
    };
    reader.readAsDataURL(selected);
  };

  const handleUrlInsert = () => {
    if (!url) return;
    const isPdf = url.toLowerCase().endsWith(".pdf");
    const snippet = isPdf 
      ? `[View PDF](${url})` 
      : `![Image](${url})`;
    onInsert(snippet);
    setUrl("");
    setShowExternal(false);
  };

  return (
    <div style={{ background: "var(--cream)", border: "1px solid var(--border)", borderRadius: 4, padding: "16px", marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h4 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1rem" }}>Add Media (PDF or Image)</h4>
        <button className="btn btn-ghost" style={{ fontSize: "0.7rem", padding: "4px 8px" }} onClick={() => setShowExternal(!showExternal)}>
          {showExternal ? "Upload Local" : "Use External URL"}
        </button>
      </div>

      {!showExternal ? (
        <div>
          <input type="file" accept="image/*,application/pdf" onChange={handleFileChange} style={{ fontSize: "0.85rem", width: "100%" }} />
          <p style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: 6 }}>Max size: 500KB (Local Storage limit)</p>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8 }}>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." style={{ flex: 1, padding: "8px 12px", border: "1px solid var(--border)", borderRadius: 4, fontSize: "0.85rem" }} />
          <button className="btn btn-primary" onClick={handleUrlInsert} style={{ fontSize: "0.8rem" }}>Insert</button>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, padding: "10px", background: "#fff5f5", border: "1px solid #feb2b2", borderRadius: 4 }}>
          <p style={{ color: "var(--rust)", fontSize: "0.82rem" }}>{error}</p>
          <p style={{ fontSize: "0.75rem", color: "#4a5568", marginTop: 4 }}>
            💡 Tip: Upload to <strong>Notion</strong>, <strong>Google Drive</strong>, or <strong>Imgur</strong> and paste the link.
          </p>
        </div>
      )}
    </div>
  );
}

function WriteView({ articles, navigate, onSave }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const editId = queryParams.get('editId');
  const editing = editId ? articles.find((a) => a.id === editId) : null;

  const [title, setTitle] = useState(editing?.title || "");
  const [subtitle, setSubtitle] = useState(editing?.subtitle || "");
  const [content, setContent] = useState(editing?.content || "");
  const [tags, setTags] = useState(editing?.tags?.join(", ") || "");
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleInsertMedia = (snippet) => {
    setContent((prev) => prev + "\n" + snippet + "\n");
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return alert("Title and content are required.");
    setSaving(true);
    const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
    const excerpt = content.replace(/[#*>`[\]]/g, "").split("\n").find((l) => l.trim().length > 20)?.trim().slice(0, 200) || content.slice(0, 200);
    const slug = editing ? editing.slug : slugify(title) + "-" + Date.now().toString(36);
    const now = new Date().toISOString();
    const nextPostNumber = articles.length > 0 
      ? Math.max(...articles.map(a => a.issueNumber || 0)) + 1 
      : 1;
    const article = {
      id: editing?.id || Math.random().toString(36).slice(2),
      title: title.trim(),
      subtitle: subtitle.trim(),
      content: content.trim(),
      contentHtml: mdToHtml(content.trim()),
      excerpt,
      tags: tagList,
      slug,
      publishedAt: editing?.publishedAt || now,
      updatedAt: now,
      issueNumber: editing?.issueNumber || nextPostNumber,
    };
    await onSave(article);
    setSaving(false);
    navigate(`/article/${slug}`);
  };

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "0 24px" }}>
      <div style={{ padding: "28px 0 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--border)", flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.25rem" }}>
          {editing ? "Edit Post" : `New Post №${String(articles.length + 1).padStart(3, "0")}`}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => setPreview(!preview)}>{preview ? "✏️ Edit" : "👁 Preview"}</button>
          <button className="btn btn-ghost" onClick={() => navigate('/')}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : editing ? "Update" : "Publish"}</button>
        </div>
      </div>

      {preview ? (
        <div style={{ padding: "40px 0" }}>
          <div className="fade-in" style={{ maxWidth: 700 }}>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontSize: "2.5rem", lineHeight: 1.15, marginBottom: 8 }}>{title || "Untitled"}</h1>
            {subtitle && <p style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", fontSize: "1.2rem", color: "var(--muted)", marginBottom: 20 }}>{subtitle}</p>}
            <div style={{ display: "flex", gap: 12, marginBottom: 32, paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
              {tags.split(",").map((t) => t.trim()).filter(Boolean).map((t) => <span key={t} className="tag">{t}</span>)}
            </div>
            <div className="prose" dangerouslySetInnerHTML={{ __html: mdToHtml(content) }} />
          </div>
        </div>
      ) : (
        <div style={{ padding: "32px 0" }}>
          <MediaUploader onInsert={handleInsertMedia} />
          
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Article title…" style={{ width: "100%", fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "2rem", background: "transparent", border: "none", borderBottom: "1px solid var(--border)", padding: "8px 0 16px", color: "var(--ink)", marginBottom: 16 }} />
          <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Subtitle or tagline (optional)…" style={{ width: "100%", fontFamily: "'Playfair Display',serif", fontStyle: "italic", fontSize: "1.1rem", background: "transparent", border: "none", borderBottom: "1px solid var(--border)", padding: "8px 0 16px", color: "var(--muted)", marginBottom: 16 }} />
          <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags, comma separated (e.g. tech, life, ideas)…" style={{ width: "100%", fontFamily: "'DM Mono',monospace", fontSize: "0.82rem", background: "transparent", border: "none", borderBottom: "1px solid var(--border)", padding: "8px 0 16px", color: "var(--muted)", marginBottom: 24 }} />
          <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={`Start writing your article here…\n\nMarkdown supported:\n## Heading\n**bold**, *italic*, > blockquote\n- list item\n[link text](url)`} style={{ width: "100%", minHeight: 520, fontFamily: "'DM Sans',sans-serif", fontSize: "1rem", lineHeight: 1.8, background: "var(--cream)", border: "1px solid var(--border)", borderRadius: 4, padding: "20px", resize: "vertical", color: "var(--ink)" }} />
          <p style={{ marginTop: 8, fontFamily: "'DM Mono',monospace", fontSize: "0.72rem", color: "var(--muted)" }}>
            {content.split(/\s+/).filter(Boolean).length} words · ~{readTime(content)} min read
          </p>
        </div>
      )}
    </div>
  );
}

function AnalyticsView({ articles, viewCounts }) {
  const navigate = useNavigate();
  const totalViews = Object.values(viewCounts).reduce((s, v) => s + v, 0);

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "0 24px" }}>
      <div style={{ padding: "40px 0 28px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "2rem", fontWeight: 700 }}>Analytics</h1>
          <p style={{ color: "var(--muted)", marginTop: 6 }}>Track how your readers engage with your content</p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigate('/')}>← Back</button>
      </div>

      <div className="fade-in" style={{ padding: "32px 0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16, marginBottom: 40 }}>
          {[
            { label: "Total Posts", value: articles.length },
            { label: "Total Views", value: totalViews },
            { label: "Avg Views / Post", value: articles.length ? Math.round(totalViews / articles.length) : 0 },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: "var(--cream)", border: "1px solid var(--border)", borderRadius: 4, padding: "20px 24px" }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)", marginBottom: 8 }}>{label}</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "2rem", fontWeight: 700 }}>{value.toLocaleString()}</div>
            </div>
          ))}
        </div>

        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.25rem", marginBottom: 16 }}>Post Performance</h2>
        <div style={{ border: "1px solid var(--border)", borderRadius: 4, overflow: "hidden" }}>
          {articles.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>No articles yet</div>
          ) : (
            articles.map((a, i) => {
              const v = viewCounts[a.slug] || 0;
              const pct = totalViews > 0 ? (v / totalViews) * 100 : 0;
              return (
                <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderBottom: i < articles.length - 1 ? "1px solid var(--border)" : "none", background: i % 2 === 0 ? "transparent" : "var(--cream)", flexWrap: "wrap" }}>
                  <PostNumber n={a.issueNumber} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.title}</div>
                    <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.7rem", color: "var(--muted)", marginTop: 2 }}>{formatDate(a.publishedAt)}</div>
                  </div>
                  <div style={{ width: 120, height: 6, background: "var(--border)", borderRadius: 3, flexShrink: 0 }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "var(--rust)", borderRadius: 3 }} />
                  </div>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.82rem", fontWeight: 500, minWidth: 60, textAlign: "right" }}>{v.toLocaleString()} views</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsView({ settings, setSettings, onSave, onSyncInitial }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...settings });
  const [saved, setSaved] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSave = async () => {
    await onSave(form);
    setSettings(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleSync = async () => {
    if (!window.confirm("This will upload the 10 initial articles to your Firebase cloud. Continue?")) return;
    setSyncing(true);
    await onSyncInitial();
    setSyncing(false);
    alert("Sync Complete! The initial articles are now in your cloud.");
  };

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "0 24px" }}>
      <div style={{ padding: "40px 0 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "2rem", fontWeight: 700 }}>Site Settings</h1>
        <button className="btn btn-ghost" onClick={() => navigate('/')}>← Back</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {[
          { label: "Blog Title", key: "title", placeholder: "The Daily Dispatch" },
          { label: "Tagline", key: "tagline", placeholder: "Your newsletter's one-liner…" },
          { label: "Your Name", key: "author", placeholder: "Aditi Dosi" },
        ].map(({ label, key, placeholder }) => (
          <div key={key}>
            <label style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)", display: "block", marginBottom: 8 }}>{label}</label>
            <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder} style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--border)", borderRadius: 4, background: "var(--cream)", fontFamily: "'DM Sans',sans-serif", fontSize: "1rem", color: "var(--ink)" }} />
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, paddingTop: 8 }}>
          <button className="btn btn-primary" onClick={handleSave}>{saved ? "✓ Saved!" : "Save Settings"}</button>
          <button className="btn btn-ghost" onClick={() => navigate('/')}>Cancel</button>
        </div>

        <div style={{ marginTop: 40, padding: 24, border: "1px solid var(--border)", borderRadius: 4, background: "var(--paper)" }}>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.2rem", marginBottom: 8 }}>Data Tools</h3>
          <p style={{ fontSize: "0.85rem", color: "var(--muted)", marginBottom: 16 }}>
            Missing the original Medium articles? This will push the 10 default articles to your Firebase cloud storage.
          </p>
          <button className="btn btn-ghost" onClick={handleSync} disabled={syncing}>
            {syncing ? "Syncing..." : "🔄 Sync Initial Articles to Cloud"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminLogin({ onLogin, onCancel }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const handle = () => {
    if (pw === ADMIN_PASSWORD) onLogin();
    else { setErr(true); setTimeout(() => setErr(false), 2000); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(14,11,8,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, backdropFilter: "blur(4px)" }}>
      <div className="fade-in" style={{ background: "var(--paper)", border: "1px solid var(--border)", borderRadius: 6, padding: "40px 36px", width: 360, maxWidth: "90vw" }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.5rem", fontWeight: 700, marginBottom: 8 }}>Admin Access</div>
        <p style={{ color: "var(--muted)", fontSize: "0.9rem", marginBottom: 24 }}>Enter your password to manage the blog</p>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handle()} placeholder="Password…" autoFocus style={{ width: "100%", padding: "10px 14px", border: `1px solid ${err ? "var(--rust)" : "var(--border)"}`, borderRadius: 4, background: "var(--cream)", fontFamily: "'DM Mono',monospace", fontSize: "1rem", marginBottom: 8 }} />
        {err && <p style={{ color: "var(--rust)", fontSize: "0.82rem", marginBottom: 8 }}>Incorrect password</p>}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={handle}>Login</button>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [articles, setArticles] = useState([]);
  const [settings, setSettings] = useState({ title: "", tagline: "", author: "" });
  const [viewCounts, setViewCounts] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function init() {
      const [a, s, v] = await Promise.all([fetchArticles(), fetchSiteSettings(), fetchAllViews()]);
      setArticles(a);
      setSettings(s);
      setViewCounts(v);
      setLoading(false);
    }
    init();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await deleteArticleFromCloud(id);
    setArticles(articles.filter((a) => a.id !== id));
  };

  const handleSaveArticle = async (article) => {
    await saveArticleToCloud(article);
    const exists = articles.find(a => a.id === article.id);
    if (exists) {
      setArticles(articles.map(a => a.id === article.id ? article : a));
    } else {
      setArticles([article, ...articles]);
    }
  };

  const handleRecordView = async (slug) => {
    await incrementViewCount(slug);
    setViewCounts(prev => ({ ...prev, [slug]: (prev[slug] || 0) + 1 }));
  };

  const handleSyncInitial = async () => {
    const promises = INITIAL_ARTICLES.map(a => saveArticleToCloud(a));
    await Promise.all(promises);
    const updated = await fetchArticles();
    setArticles(updated);
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--paper)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", fontStyle: "italic", color: "var(--muted)" }}>
          Loading Dispatch...
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div style={{ minHeight: "100vh", background: "var(--paper)" }}>
        {showLogin && <AdminLogin onLogin={() => { setIsAdmin(true); setShowLogin(false); }} onCancel={() => setShowLogin(false)} />}

        <Header settings={settings} isAdmin={isAdmin} onAdmin={() => setShowLogin(true)} />

        <Routes>
          <Route path="/" element={<HomeView articles={articles} settings={settings} isAdmin={isAdmin} viewCounts={viewCounts} onDelete={handleDelete} />} />
          <Route path="/article/:slug" element={<ArticleView articles={articles} settings={settings} onRecordView={handleRecordView} />} />
          <Route path="/archive" element={<ArchiveView articles={articles} settings={settings} />} />
          <Route path="/write" element={isAdmin ? <WriteConsumer articles={articles} onSave={handleSaveArticle} /> : <HomeView articles={articles} settings={settings} isAdmin={isAdmin} viewCounts={viewCounts} onDelete={handleDelete} />} />
          <Route path="/analytics" element={isAdmin ? <AnalyticsView articles={articles} viewCounts={viewCounts} /> : <HomeView articles={articles} settings={settings} isAdmin={isAdmin} viewCounts={viewCounts} onDelete={handleDelete} />} />
          <Route path="/settings" element={isAdmin ? <SettingsView settings={settings} setSettings={setSettings} onSave={saveSettingsToCloud} onSyncInitial={handleSyncInitial} /> : <HomeView articles={articles} settings={settings} isAdmin={isAdmin} viewCounts={viewCounts} onDelete={handleDelete} />} />
        </Routes>
      </div>
    </Router>
  );
}

// Helper to use navigate in WriteView
function WriteConsumer({ articles, onSave }) {
  const navigate = useNavigate();
  return <WriteView articles={articles} navigate={navigate} onSave={onSave} />;
}
